import { forwardRef, useEffect, useImperativeHandle, useState } from 'react';
import { DatePicker, FormField, FormInput, FormSectionTitle, FormSelect } from '@practium/ui';
import { formatDateFromISO } from '@practium/shared';
import type { SectorFormData, PropertyPageMode, AreaDTO } from '../../../types';
import { useSectorForm } from '../../../hooks/useSectorForm';
import styles from '../PropertyForm.module.css';
import { IrrigationFields, IrrigationViewFields } from './IrrigationsFields';

export interface SectorFormRef {
	submit: () => Promise<void>;
	isDirty: () => boolean;
}

interface SectorFormProps {
	mode: PropertyPageMode;
	initialData?: SectorFormData;
	availableAreas?: AreaDTO[];
	propId?: string;
	propertyName?: string;
	onSubmit: (createdId?: string) => void;
	onLoadingChange: (loading: boolean) => void;
	avatarfile?: File | null;
}

export const SectorForm = forwardRef<SectorFormRef, SectorFormProps>(function SectorForm(
	{
		mode,
		initialData,
		availableAreas = [],
		propId,
		propertyName,
		onSubmit,
		onLoadingChange,
		avatarfile,
	},
	ref
) {
	const [currentPropId, setCurrentPropId] = useState<string>(propId || '');
	const {
		form,
		errors,
		isLoading,
		set,
		setIrrigation,
		handleSubmit,
		isEditing,
		replicateCount,
		setReplicateCount,
		isDirty,
	} = useSectorForm({
		initialData,
		propId: currentPropId,
		onSubmit,
		avatarFile: avatarfile,
	});

	const selectedArea = availableAreas.find(
		(a) => (a._id ?? a.name) === form.areaId
	);

	const irrigationType = selectedArea?.irrigation ?? 'none';
	const hasIrrigation = irrigationType !== 'none';

	useEffect(() => {
		if (selectedArea?.propId) {
			setCurrentPropId(selectedArea.propId);
		}
	}, [selectedArea?.propId]);

	useImperativeHandle(ref, () => ({ submit: handleSubmit, isDirty }));

	useEffect(() => {
		onLoadingChange(isLoading);
	}, [isLoading, onLoadingChange]);

	const areaOptions = availableAreas.map((a) => ({ value: a._id ?? a.name, label: a.name }));

	const isView = mode === 'view';

	if (isView) {
		const areaLabel =
			form.areaName ??
			availableAreas.find((a) => (a._id ?? a.name) === form.areaId)?.name ??
			form.areaId ??
			'—';
		return (
			<div className={styles.viewGrid}>
				<div className={styles.viewField}>
					<span className={styles.viewLabel}>Nome</span>
					<span className={styles.viewValue}>{form.name || '—'}</span>
				</div>
				<div className={styles.viewField}>
					<span className={styles.viewLabel}>Área</span>
					<span className={styles.viewValue}>{areaLabel}</span>
				</div>
				<div className={styles.viewField}>
					<span className={styles.viewLabel}>Data de Plantio</span>
					<span className={styles.viewValue}>{formatDateFromISO(form.plantingDate)}</span>
				</div>
				<div className={styles.viewField}>
					<span className={styles.viewLabel}>Tamanho</span>
					<span className={styles.viewValue}>{form.size ? `${form.size} ha` : '—'}</span>
				</div>
				<div className={styles.viewField}>
					<span className={styles.viewLabel}>Espaçamento entre Linhas</span>
					<span className={styles.viewValue}>
						{form.lineSpacing ? `${form.lineSpacing} m` : '—'}
					</span>
				</div>
				<div className={styles.viewField}>
					<span className={styles.viewLabel}>Espaçamento entre Plantas</span>
					<span className={styles.viewValue}>
						{form.plantSpacing ? `${form.plantSpacing} m` : '—'}
					</span>
				</div>
				<div className={styles.viewField}>
					<span className={styles.viewLabel}>Quantidade de Plantas</span>
					<span className={styles.viewValue}>
						{form.amountOfPlants ? form.amountOfPlants.toLocaleString('pt-BR') : '—'}
					</span>
				</div>
				<div className={styles.viewField}>
					<span className={styles.viewLabel}>Propriedade</span>
					<span className={styles.viewValue}>{propertyName || '—'}</span>
				</div>
				{hasIrrigation && (
					<>
						<div className={styles.sectionBreak}>
							<FormSectionTitle>Informações de Irrigação</FormSectionTitle>
						</div>
						<IrrigationViewFields irrigationType={irrigationType} irrigation={form.irrigation} />
					</>
				)}
			</div>
		);
	}

	return (
		<div className={styles.formGrid}>
			<FormField label="Nome" required error={errors.name}>
				<FormInput
					value={form.name}
					onChange={(e) => set('name', e.target.value)}
					placeholder="Ex.: AJ-32"
					hasError={!!errors.name}
				/>
			</FormField>
			<FormField label="Área" required error={errors.areaId}>
				<FormSelect
					value={form.areaId}
					onChange={(v) => set('areaId', v)}
					options={areaOptions}
					placeholder="Selecione a Área"
					hasError={!!errors.areaId}
					searchable
					disabled={isEditing}
				/>
			</FormField>
			<FormField label="Data de Plantio" required error={errors.plantingDate}>
				<DatePicker
					value={form.plantingDate}
					onChange={(v) => set('plantingDate', v)}
					hasError={!!errors.plantingDate}
				/>
			</FormField>
			<FormField label="Tamanho" required error={errors.size}>
				<FormInput
					value={form.size}
					type="number"
					onChange={(e) => set('size', e.target.value)}
					placeholder="Ex.: 0.6"
					hasError={!!errors.size}
					suffix="ha"
				/>
			</FormField>
			<FormField label="Espaçamento entre Linhas" required error={errors.lineSpacing}>
				<FormInput
					value={form.lineSpacing}
					type="number"
					onChange={(e) => set('lineSpacing', e.target.value)}
					placeholder="Ex.: 5.0"
					hasError={!!errors.lineSpacing}
					suffix="m"
				/>
			</FormField>
			<FormField label="Espaçamento entre Plantas" required error={errors.plantSpacing}>
				<FormInput
					value={form.plantSpacing}
					type="number"
					onChange={(e) => set('plantSpacing', e.target.value)}
					placeholder="Ex.: 1.0"
					hasError={!!errors.plantSpacing}
					suffix="m"
				/>
			</FormField>
			<FormField label="Quantidade de Plantas" required>
				<FormInput
					value={form.amountOfPlants > 0 ? String(form.amountOfPlants) : ''}
					type="number"
					onChange={() => { }}
					placeholder="Calculado automaticamente"
					readOnly
				/>
			</FormField>
			<FormField label="Replicar setor? Se sim, quantas vezes?">
				<FormInput
					value={replicateCount > 0 ? String(replicateCount) : ''}
					type="number"
					min={0}
					onChange={(e) => setReplicateCount(Number(e.target.value) || 0)}
					placeholder="Ex.: 3"
				/>
			</FormField>
			<FormField label="Propriedade">
				<FormInput value={propertyName ?? ''} readOnly className={styles.readonlyInput} />
			</FormField>
			{hasIrrigation && (
				<>
					<div className={styles.sectionBreak}>
						<FormSectionTitle>Informações de Irrigação</FormSectionTitle>
					</div>
					<IrrigationFields
						irrigationType={irrigationType}
						irrigation={form.irrigation}
						setIrrigation={setIrrigation}
					/>
				</>
			)}
		</div>
	);
});
